import {canBePickedUp} from "../libraries/utils";
import {generateRandomNumber} from "../libraries/globalTemplateSpecific/bm100/utils";
import {mapGetters} from "vuex";
import appConfig from "../appconfig";
import MysteryBoxProvider from "../providers/MysteryBoxProvider";
import {SET_VOUCHER_VIRTUAL_ORDER_ID} from "../store/mutation-types";
import * as mutationTypes from "../store/mutation-types";
import {getShopPattern} from "../domains2/priceRules/priceRulesHelpers";

const MYSTERY_BOX_LABELS = Object.freeze({
	MYSTERY_BOX_RANDOM_ACT_OF_KINDNESS: "MysteryBoxRandomActOfKindness",
	MYSTERY_BOX_LOWER: "MysteryBoxLower",
	MYSTERY_BOX_HIGHER: "MysteryBoxHigher"
});

export default {
	computed: {
		...mapGetters([
			"priceRules",
			"productCatalog",
			"tenantSettings",
			"tenantSpecificData",
			"selections",
			"mysteryBoxModalState",
			"isSubscribed",
			"applicationUserLoggedIn",
			"customerDetails",
			"globalSelectedShop"
		]),
		mysteryBoxSettings() {
			return this.tenantSettings.mysteryBox || {};
		},
		getMysteryBoxPriceRuleLabel() {
			if(this.globalSelectedShop.shopOptions.enableMysteryBoxRandomActOfKindness) {
				return MYSTERY_BOX_LABELS.MYSTERY_BOX_RANDOM_ACT_OF_KINDNESS;
			}
			return this.totalPrice < (this.mysteryBoxSettings.mysteryBoxPriceThreshold || 0)
				? MYSTERY_BOX_LABELS.MYSTERY_BOX_LOWER
				: MYSTERY_BOX_LABELS.MYSTERY_BOX_HIGHER;
		},
		checkMysteryBoxCriteria() {
			if(this.globalSelectedShop.isOpeningLaterToday) {
				return false;
			}
			if(this.globalSelectedShop.shopOptions.enableMysteryBoxRandomActOfKindness && this.globalSelectedShop.shopOptions.enableMysteryBox) {
				return false;
			}
			return !!((this.globalSelectedShop.shopOptions.enableMysteryBoxRandomActOfKindness || this.globalSelectedShop.shopOptions.enableMysteryBox)
				&& !this.selections.find(s => s.mysteryBoxAppliedVoucherId)
				&& this.mysteryBoxModalState
				&& this.cartProducts.length);
		}
	},
	methods: {
		async getUserPreviousOrderItems(mysteryBoxProducts, voucherId) {
			try {
				const response = await MysteryBoxProvider.getMysteryBoxData({
					email: this.applicationUserLoggedIn ? this.customerDetails && this.customerDetails.email : "",
					mysteryBoxProducts,
					voucherId,
					shopId: this.globalSelectedShop.id,
					shopTimeZone: this.globalSelectedShop.shopTimeZone,
					label: this.getMysteryBoxPriceRuleLabel
				});
				return response && response.data;
			} catch (e) {
				return {};
			}
		},
		getMysteryBoxProducts(priceRule) {
			return ((priceRule && priceRule.productPattern) || []).filter(productId => {
				try {
					const product = this.productCatalog.getProduct(productId);
					// if product is not available on certain channel, it should be removed
					if(product.options.deliveryMethodIndex) {
						if(!product.options.deliveryMethodIndex.includes(this.tenantSpecificData.deliveryChoice)) {
							return false;
						}
					}
					const categoriesOfProduct = this.productCatalog.getAllCategoriesOfProduct(productId);
					const isProductCategoriesContainSelectedShop = categoriesOfProduct.some(
						(category) => Array.isArray(category.isSpecificForShops)
							&& category.isSpecificForShops.includes(this.globalSelectedShop.id)
					);
					const isProductSupportedByActiveChannel = !categoriesOfProduct.length || categoriesOfProduct
						.filter(category => !category.channels || category.channels.includes(appConfig.channelAlias()))
						.length;
					// this code covers using only products which are assigned to categories for specified channel
					if(!isProductSupportedByActiveChannel || !isProductCategoriesContainSelectedShop) {
						return false;
					}
					return canBePickedUp(
						product,
						this.productCatalog,
						this.$store.getters.cachedPickupRules,
						this.globalSelectedShop.id
					);
				} catch (e) {
					// should skip not available products in the shop
					return undefined;
				}
			})
				.filter(productId => {
					// product already in cart should not be shown in mystery box
					return !this.cartProducts.find(cartProduct => cartProduct.productId === productId);
				});
		},
		findPriceRule() {
			return (this.priceRules || []).find((pr) => {
				const priceRuleChannelAvailability = !pr.options.channels
					|| !pr.options.channels.length
					|| pr.options.channels.includes(appConfig.channelAlias());
				const shopPattern = getShopPattern(pr.shopPattern);
				return pr && pr.options.label === this.getMysteryBoxPriceRuleLabel
					&& (shopPattern || []).includes(this.globalSelectedShop.id)
					&& priceRuleChannelAvailability;
			});
		},
		async getMysteryBoxProduct() {
			if(!this.checkMysteryBoxCriteria) {
				return null;
			}
			const priceRule = this.findPriceRule();
			if(!priceRule || !["simpleDiscountFixedAmount", "percentDiscount"].includes(priceRule.discountType) || !priceRule.voucherPattern.length) {
				return null;
			}
			this.applicableMysteryBoxPriceRule = priceRule;
			const mysteryBoxProducts = this.getMysteryBoxProducts(priceRule);
			if(!mysteryBoxProducts.length) {
				return null;
			}
			// special random act of kindness mystery box
			if(this.globalSelectedShop.shopOptions.enableMysteryBoxRandomActOfKindness) {
				const excludedProducts = priceRule.options.mysteryBoxExcludedProducts && priceRule.options.mysteryBoxExcludedProducts.length
					? priceRule.options.mysteryBoxExcludedProducts
					: null;
				return this.handleRandomActOfKindness(
					excludedProducts || mysteryBoxProducts, // we agreed to fall back to price rule products if there is no excluded set
					priceRule,
					mysteryBoxProducts
				);
			}
			// regular mystery box
			const mysteryBoxProductId = mysteryBoxProducts[generateRandomNumber(mysteryBoxProducts.length)];
			const checkVoucher = await MysteryBoxProvider.getMysteryBoxData({
				email: this.applicationUserLoggedIn ? this.customerDetails && this.customerDetails.email : "",
				voucherId: priceRule.voucherPattern[0],
				shopId: this.globalSelectedShop.id,
				shopTimeZone: this.globalSelectedShop.shopTimeZone,
				label: this.getMysteryBoxPriceRuleLabel
			});
			if(!checkVoucher || !checkVoucher.data.voucherId) {
				return false;
			}
			if(checkVoucher && checkVoucher.data.voucherVirtualOrderId) {
				this.$store.commit(SET_VOUCHER_VIRTUAL_ORDER_ID, checkVoucher.data.voucherVirtualOrderId);
				setTimeout(() => {
					if (this.mysteryBoxModalState && !(this.selections || []).find(s => s.mysteryBoxAppliedVoucherId)) {
						this.closeMysteryBoxModal();
					}
				}, this.mysteryBoxSettings.mysteryBoxVoucherReleaseTimeout || 300000);
			}
			if(mysteryBoxProductId) {
				this.mysteryBoxProductId = mysteryBoxProductId;
				return mysteryBoxProductId;
			}
			return null;
		},
		async handleRandomActOfKindness(excludedProducts, priceRule, mysteryBoxProducts) {
			this.sendingOrder = true;
			const {voucherVirtualOrderId} = await this.getUserPreviousOrderItems(
				excludedProducts,
				priceRule.voucherPattern[0]
			);
			if (!voucherVirtualOrderId) {
				return null;
			}
			const availableProductId = mysteryBoxProducts[generateRandomNumber(mysteryBoxProducts.length)];
			this.$store.commit(SET_VOUCHER_VIRTUAL_ORDER_ID, voucherVirtualOrderId);
			this.mysteryBoxProductId = availableProductId;
			this.sendingOrder = false;
			setTimeout(() => {
				if (!this.mysteryBoxModalState && !(this.selections || []).find(s => s.mysteryBoxAppliedVoucherId)) {
					this.closeMysteryBoxModal();
				}
			}, this.mysteryBoxSettings.mysteryBoxVoucherReleaseTimeout || 300000);
			this.$store.commit(mutationTypes.SET_MYSTERY_BOX_MODAL_STATE);
			return availableProductId;
		}
	}
};
